import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@volca/ui/components/ui/dropdown-menu";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { reportError } from "@/profitability/utils";
import { EXPLAIN_REASONS, FeedbackDialog } from "./feedback-dialog";
import { RESOLUTION_LABEL, STATUS_LABEL } from "./labels";
import { useUpdateFinding } from "./query";
import type { Finding, FindingResolution, FindingStatus } from "./types";

// Literal classes, so Tailwind keeps them. Colour says where it stands: red
// needs someone, blue is being looked at, green is done. Amber stays the
// flag's alone.
export const STATUS_DOT: Record<FindingStatus, string> = {
  open: "bg-line-strong",
  investigating: "bg-information",
  action_required: "bg-danger",
  explained: "bg-subtlest",
  resolved: "bg-success",
};

export const StatusDot = ({ status }: { status: FindingStatus }) => (
  <span
    aria-hidden
    className={cn("size-2 flex-none rounded-full", STATUS_DOT[status])}
  />
);

const ORDER: FindingStatus[] = [
  "open",
  "investigating",
  "action_required",
  "explained",
  "resolved",
];

/**
 * A finding's place in its lifecycle, and the menu that moves it: being
 * looked into, needing action, explained (with why), resolved. Explaining
 * asks why, because that is what keeps it from flagging the same way again.
 */
export const FindingStatusMenu = ({
  businessId,
  finding,
  size = "sm",
  aligned = false,
}: {
  businessId: string;
  finding: Pick<Finding, "id" | "status" | "resolution">;
  size?: "xs" | "sm";
  /** One width whatever the status, so a list's controls line up row to row. */
  aligned?: boolean;
}) => {
  const update = useUpdateFinding(businessId);
  const [explaining, setExplaining] = useState(false);
  const move = (
    status: FindingStatus,
    resolution?: FindingResolution,
    note?: string,
  ) =>
    update.mutate(
      { findingId: finding.id, status, resolution, note },
      {
        onSuccess: () => setExplaining(false),
        onError: (error) =>
          toast.error("The finding could not be updated", {
            description: reportError(error),
          }),
      },
    );
  const label =
    finding.status === "explained" && finding.resolution
      ? RESOLUTION_LABEL[finding.resolution]
      : STATUS_LABEL[finding.status];
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size={size}
              disabled={update.isPending}
              aria-label={`Status: ${label}. Change status`}
              className={cn("gap-1.5 text-subtle", {
                "min-w-32 justify-start": aligned,
              })}
            />
          }
        >
          <StatusDot status={finding.status} />
          <span className="text-ink">{label}</span>
          <CaretDownIcon aria-hidden className={cn({ "ms-auto": aligned })} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-48 pointer-coarse:[&_[role^=menuitem]]:min-h-11"
        >
          {ORDER.filter((status) => status !== finding.status).map((status) => (
            <span key={status} className="contents">
              {status === "explained" && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() =>
                  status === "explained"
                    ? setExplaining(true)
                    : status === "resolved"
                      ? move("resolved", "fixed")
                      : move(status)
                }
              >
                <StatusDot status={status} />
                {status === "explained"
                  ? "Explained…"
                  : status === "resolved"
                    ? "Fixed"
                    : status === "open"
                      ? "Reopen"
                      : STATUS_LABEL[status]}
              </DropdownMenuItem>
            </span>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <FeedbackDialog
        open={explaining}
        onOpenChange={setExplaining}
        title="Why is this explained?"
        reasons={EXPLAIN_REASONS}
        submitLabel="Mark explained"
        pending={update.isPending}
        onSubmit={({ reason, note }) =>
          move("explained", reason as FindingResolution, note || undefined)
        }
      />
    </>
  );
};
